import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from '../../../../../../common/services';
import { CustomHttpErrorResponse, Error } from '../../../../../../common/constants';
import { ListServerPaginationStateModel, CarrierModel } from '../../../../../../common/models';
import { HubLoanerPhoneListItem, YardSpotConfigurationModel } from '../../../../administration/hub/models';
import { UliDetailsModel, AllocationViewUliCarrierModel } from '../../models';
import { AllocationViewRegisterActionService } from './allocation-view-register-action.service';
import {
  AllocationViewRegisterActionPrepare,
  AllocationViewRegisterActionUliRegister,
  AllocationViewRegisterActionLoadLoanerPhones,
  AllocationViewRegisterActionLoadCarries,
  AllocationViewRegisterActionSetInitialState,
  AllocationViewRegisterActionLoadParkingSpots,
} from './allocation-view-register-action.actions';
import { AllocationViewActionsCarriersService } from '../services';
import { AllocationViewActionBaseState } from '../base';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface AllocationViewRegisterActionStateModel {
  loaded: boolean;
  loanerPhones: HubLoanerPhoneListItem[];
  preparedUliDetails: UliDetailsModel;
  carriers: AllocationViewUliCarrierModel[];
  parkingSpots: YardSpotConfigurationModel[];
}

const initialState: AllocationViewRegisterActionStateModel = {
  loaded: false,
  loanerPhones: [],
  preparedUliDetails: null,
  carriers: [],
  parkingSpots: [],
};

@State({
  name: 'allocationViewActionsRegister',
  defaults: initialState,
})
@Injectable()
export class AllocationViewRegisterActionState extends AllocationViewActionBaseState {
  @Selector()
  static getDetails(state: AllocationViewRegisterActionStateModel): UliDetailsModel {
    return state.preparedUliDetails;
  }

  @Selector()
  static getLoanerPhones(state: AllocationViewRegisterActionStateModel): HubLoanerPhoneListItem[] {
    return state.loanerPhones;
  }

  @Selector()
  static getCarriers(state: AllocationViewRegisterActionStateModel): AllocationViewUliCarrierModel[] {
    return state.carriers;
  }

  @Selector()
  static getLoaded(state: AllocationViewRegisterActionStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getParkingSpots(state: AllocationViewRegisterActionStateModel): YardSpotConfigurationModel[] {
    return state.parkingSpots;
  }

  constructor(
    private allocationViewRegisterActionService: AllocationViewRegisterActionService,
    private allocationViewActionsCarriersService: AllocationViewActionsCarriersService,
    notificationService: NotificationService,
    store: Store,
  ) {
    super(notificationService, store);
  }

  @Action(AllocationViewRegisterActionPrepare)
  getPreparedUliDetails(
    ctx: StateContext<AllocationViewRegisterActionStateModel>,
    { payload }: AllocationViewRegisterActionPrepare,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewRegisterActionService.getPreparedUliDetails(payload).pipe(
      map((preparedUliDetails: UliDetailsModel) => {
        ctx.patchState({ preparedUliDetails, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewRegisterActionLoadParkingSpots)
  getParkingSpots(
    ctx: StateContext<AllocationViewRegisterActionStateModel>,
    { payload }: AllocationViewRegisterActionLoadParkingSpots,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewRegisterActionService.getParkingSpots(payload).pipe(
      map((parkingSpots: YardSpotConfigurationModel[]) => {
        ctx.patchState({ parkingSpots, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewRegisterActionUliRegister)
  registerUli(
    ctx: StateContext<AllocationViewRegisterActionStateModel>,
    { payload: { uli } }: AllocationViewRegisterActionUliRegister,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewRegisterActionService.uliRegister(uli).pipe(
      map(() => {
        this.finishAction('api.uli.success.register');
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewRegisterActionLoadLoanerPhones)
  getLoanerPhones(
    ctx: StateContext<AllocationViewRegisterActionStateModel>,
    { payload }: AllocationViewRegisterActionLoadLoanerPhones,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewRegisterActionService.getLoanerPhones(payload.hubId, payload.searchStr).pipe(
      map((loanerPhones: ListServerPaginationStateModel<HubLoanerPhoneListItem>) => {
        ctx.patchState({
          loanerPhones: [...loanerPhones.items],
        });
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewRegisterActionLoadCarries)
  getCarriers(
    ctx: StateContext<AllocationViewRegisterActionStateModel>,
    { payload }: AllocationViewRegisterActionLoadCarries,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewActionsCarriersService.getCarriers(payload).pipe(
      map((carriers: CarrierModel[]) => {
        ctx.patchState({
          carriers: [
            ...carriers.map((carrier: CarrierModel) => ({
              carrierExtId: carrier.extId,
              carrierName: carrier.name,
              carrierNumber: carrier.number,
            })),
          ],
        });
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewRegisterActionSetInitialState)
  setInitialState(ctx: StateContext<AllocationViewRegisterActionStateModel>): void {
    ctx.patchState(initialState);
    this.setContainerAfterActionEffectsInitialState();
  }
}
